import { watch } from "vue";
import { ngFrameworkLoaded } from "@sazka/ng/ng-integration";
import { useNgSession } from "@sazka/ng/ng-session";
import {
    useChat as useBaseChat,
    type ChatPlayerData,
    setChatSessionWatcher,
} from "@sazka/shared-components/layout/fixed-help/composables/use-chat";
import { useDWH } from "../dwh/use-dwh";
import { useExponea } from "../exponea/use-exponea";

/**
 * Web-specific chat integration that wraps base useChat with NG session data.
 * Use this in web components instead of calling useChat() directly.
 */
export const useChatIntegration = () => {
    const baseChat = useBaseChat();
    const { session, balanceNumeric, balance, isLoggedIn } = useNgSession();

    const getNgPlayerData = (): ChatPlayerData => {
        return {
            Username: session.value?.PlayerEmail || "",
            PlayerID: session.value?.PlayerID || "",
            FirstName: session.value?.PlayerName || "",
            Language: session.value?.PlayerLanguage || "CZE",
            Balance: (balanceNumeric.value / 100).toFixed(2) || "",
            Currency: balance.value?.Currency || "CZK",
            VipLevel: session.value?.PlayerVipLevel || "",
            Depositor: isLoggedIn.value ? "Y" : "N",
            SessionKey: session.value?.SessionToken,
        };
    };

    const showWithSession = async (isProactiveChat = false, campaignCode?: string, creativeCode?: string) => {
        await ngFrameworkLoaded();
        const playerData = getNgPlayerData();

        console.log("[WebChat] Showing chat with NG player data:", {
            hasPlayerID: !!playerData.PlayerID,
            isProactiveChat,
            campaignCode,
        });

        await baseChat.show(isProactiveChat, campaignCode, creativeCode, playerData);
    };

    const setupNgSessionWatcher = async (sceInstance: any) => {
        await ngFrameworkLoaded();

        watch(
            session,
            (newValue, oldValue) => {
                if (!sceInstance.notifySessionChange) return;
                if (newValue?.PlayerID == oldValue?.PlayerID) return;

                const playerId = newValue?.PlayerID || oldValue?.PlayerID;
                sceInstance.notifySessionChange(
                    playerId,
                    newValue?.PlayerID ? "login" : "logout",
                    session.value?.SessionToken || undefined
                );
            },
            { immediate: true }
        );
    };

    const registerWithNgFramework = async () => {
        await ngFrameworkLoaded();
        ng_hs.onShowChat(() => {
            void showWithSession();
        });
    };

    const registerWindowIntegrations = () => {
        window.showChat = (options?: { isProactiveChat?: boolean; campaignCode?: string; creativeCode?: string }) => {
            void showWithSession(options?.isProactiveChat || false, options?.campaignCode, options?.creativeCode);
        };

        window.callShowChat = (campaignCode?: string) => {
            void showWithSession(false, campaignCode);
        };
    };

    const registerExternalIntegrations = () => {
        const handleExternalShowChat = (settings: {
            /* eslint-disable @typescript-eslint/naming-convention */
            ProactiveChat: string;
            campaignCode: string;
            creativeCode: string;
        }) => {
            void showWithSession(settings?.ProactiveChat === "true", settings?.campaignCode, settings?.creativeCode);
        };

        useExponea().expose("showChat", handleExternalShowChat);
        useDWH().expose("showChat", handleExternalShowChat);
    };

    /**
     * Initialize all chat integrations in one call.
     * This sets up NG framework integration, window integrations, external integrations,
     * and the session watcher for the chat.
     */
    const setupAll = () => {
        setChatSessionWatcher(setupNgSessionWatcher);
        registerWindowIntegrations();
        void registerWithNgFramework();
        registerExternalIntegrations();
    };

    return {
        // Main function for web components
        showWithSession,

        // Setup functions
        setupNgSessionWatcher,
        registerWithNgFramework,
        registerWindowIntegrations,
        registerExternalIntegrations,

        // All-in-one setup
        setupAll,
    };
};
